<script setup>
import { computed } from 'vue'

const props = defineProps({
  model: {
    type: Object,
    default: () => ({}),
  },
  iconSrc: {
    type: String,
  },
  iconWidth: {
    type: String,
  },
  iconHeight: {
    type: String,
  },
  iconAltText: {
    type: String,
  },
  title: {
    type: String,
  },
  summary: {
    type: String,
  },
  questions: {
    type: Array,
  },
  buttonText: {
    type: String,
  },
  buttonUrl: {
    type: String,
  },
  width: {
    type: String,
    default: null,
  },
  customBottomSpace: {
    type: String,
    default: null,
  },
  bottomBorder: {
    type: Boolean,
    default: false,
  },
})

const effectiveIconSrc = computed(() => props.iconSrc || props.model?.iconSrc?.value)
const effectiveTitle = computed(() => props.title || props.model?.title?.value)
const effectiveSummary = computed(() => props.summary || props.model?.summary?.value)
const effectiveQuestions = computed(() => props.questions || props.model?.questionList?.value)
const effectiveButtonText = computed(() => props.buttonText || props.model?.buttonText?.value)
const effectiveButtonUrl = computed(() => props.buttonUrl || props.model?.buttonUrl?.value)

const questionLinks = ref([])

const listingGuid = computed(() => props.model?.contentLink?.guidValue)

if (listingGuid?.value) {
  // Fetch related questions from API using the content link ID
  const { data: questionLinksResponse, error: fetchError } = useFetch('/api/questions/questionsByQuestionsBlock', {
    query: {
      listingGuid,
    },
    method: 'GET',
  })

  // Process questions data when it becomes available
  watchEffect(() => {
    if (fetchError.value) {
      console.error('Failed to fetch questions:', fetchError.value)
      questionLinks.value = []
      return
    }

    if (questionLinksResponse.value) {
      questionLinks.value = questionLinksResponse?.value?.length ? questionLinksResponse.value : []

      effectiveQuestions.value.push(...questionLinks.value)
    }
  })
}
</script>

<template>
  <!-- Questions Block -->
  <section
    class="questions-block flex flex-wrap mx-auto"
    :class="{
      'w-full container-text eight-col-max': !width || width === '',
      'w-full container-narrow': width === 'wide',
      'block-spacing': !customBottomSpace || customBottomSpace === '',
    }"
  >
    <div class="questions-block__header flex-1 w-full md:flex-auto">
      <h2
        v-if="effectiveTitle"
        class="questions-block__title text-2xl font-bold mb-2"
      >
        {{ effectiveTitle }}
      </h2>
      <p
        v-if="effectiveSummary"
        class="questions-block__summary text-base mb-6"
      >
        {{ effectiveSummary }}
      </p>
    </div>

    <div
      v-if="effectiveIconSrc"
      class="questions-block__icon ml-4 md:ml-0 md:flex-none md:mr-8"
    >
      <NuxtImg
        provider="fastlyAprimo"
        :src="iconSrc"
        :alt="iconAltText"
        class="mb-4 max-w-20 h-auto"
        :width="iconWidth ?? 80"
        :height="iconHeight ?? 80"
      />
    </div>

    <div class="questions-block__body w-100 flex-none basis-full md:flex-1">
      <div class="questions-block__questions md:flex md:flex-wrap md:-mx-6">
        <div
          v-for="(question, index) in effectiveQuestions"
          :key="index"
          class="questions-block__question md:w-1/2 mb-3 md:mb-6 md:px-6"
        >
          <ULink
            :to="useCmsUrl(question?.href)"
            :locale="false"
            class="questions-block__question-link underline font-bold text-med-dark-gray"
          >
            {{ question.text }}
          </ULink>
        </div>
      </div>

      <div
        v-if="effectiveButtonText"
        class="questions-block__footer w-full pt-3 md:pt-6 md:flex"
      >
        <UButton
          v-if="effectiveButtonText"
          :to="useCmsUrl(effectiveButtonUrl)"
          :locale="false"
          variant="solid"
          color="primary"
          class="questions-block__footer-button px-10"
        >
          {{ effectiveButtonText }}
        </UButton>
      </div>
    </div>

    <hr
      v-if="bottomBorder"
      class="border-b border-med-aqua mt-4 mb-6 w-full md:mt-8 md:mb-10 lg:mt-12 lg:mb-14"
    >
  </section>
</template>
